import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  MapPin,
  Clock,
  Calendar,
  MessageSquare,
  Users,
  DollarSign,
  Star,
  Shield,
  Award,
  GraduationCap,
  CheckCircle,
  ArrowLeft,
  AlertCircle,
} from 'lucide-react';
import { ConfirmModal } from '@tutor/ui';
import type { Tutor } from '../types';
import { useCaseDetail } from '@/hooks/api/useCaseDetail';
import { useTutorCompletionStatus } from '@/hooks/api/useTutorCompletionStatus';
import {
  useParentCaseShortlists,
  useRespondToParentShortlistMutation,
} from '@/hooks/api/useShortlist';
import { useAuthContext } from '../contexts/useAuthContext';
import { CaseDetailSkeleton } from '../components/skeletons';
import type {
  ParentCaseShortlistData,
  ParentShortlistRespondRequest,
} from '@/types/api';
import { useI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';
import { useNavigate } from '@/i18n/navigation';

const SHORTLIST_STATUS_LABELS: Record<ParentCaseShortlistData['status'], string> = {
  pending: 'Shortlisted',
  contacting: 'Contacting Tutor',
  accepted: 'Ready for Your Decision',
  declined: 'Rejected',
};

const SHORTLIST_STATUS_STYLES: Record<ParentCaseShortlistData['status'], string> = {
  pending: 'bg-amber-100 text-amber-800',
  contacting: 'bg-blue-100 text-blue-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-gray-200 text-gray-700',
};

const staticCardClassName = 'overflow-hidden rounded-xl bg-white shadow-md';
const staticTintedCardClassName = 'overflow-hidden rounded-xl border border-blue-200 bg-blue-50 shadow-md';

const formatEducationQualifications = (shortlist: ParentCaseShortlistData) => {
  const university = shortlist.school?.trim();
  const fieldOfStudy = shortlist.major?.trim();

  if (university && fieldOfStudy) {
    return `${university} · ${fieldOfStudy}`;
  }

  return university || fieldOfStudy || 'Not provided';
};

const ParentShortlistCard: React.FC<{
  shortlist: ParentCaseShortlistData;
  caseMatchedTutorId?: string | null;
  onSelectAction: (
  shortlist: ParentCaseShortlistData,
  action: ParentShortlistRespondRequest['status']
  ) => void;
  isUpdating: boolean;
  onOpenTutorProfile: (tutorId: string) => void;
}> = ({ shortlist, caseMatchedTutorId, onSelectAction, isUpdating, onOpenTutorProfile }) => {
  const { locale } = useI18n();
  const isMatched = caseMatchedTutorId === shortlist.tutorId;
  const canRespond = !isMatched && shortlist.status !== 'declined';
  const isZh = locale === 'zh-CN';
  const statusLabels: Record<ParentCaseShortlistData['status'], string> = isZh
    ? {
        pending: '已 shortlist',
        contacting: '联系导师中',
        accepted: '等待你决定',
        declined: '已拒绝',
      }
    : SHORTLIST_STATUS_LABELS;

  return (
    <div
      onClick={() => onOpenTutorProfile(shortlist.tutorId)}
      className="cursor-pointer rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold text-gray-900">{shortlist.tutorName}</h3>
            <span
              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${SHORTLIST_STATUS_STYLES[shortlist.status]}`}
            >
              {isMatched ? (isZh ? '已接受' : 'Accepted') : statusLabels[shortlist.status]}
            </span>
            {shortlist.isFullyVerified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
                <CheckCircle className="h-3.5 w-3.5" />
                {isZh ? '已验证' : 'Verified'}
              </span>
            )}
          </div>

          <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
            <span className="font-semibold text-gray-900">{isZh ? '学历与资历：' : 'Education & Qualifications:'}</span>{' '}
            {isZh
              ? localizeText(locale, formatEducationQualifications(shortlist))
              : formatEducationQualifications(shortlist)}
          </div>

          {shortlist.reason && (
            <div className="mt-4 rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
              <span className="font-semibold text-gray-900">{isZh ? '我们为什么 shortlist 这位导师：' : 'Why we shortlisted this tutor:'}</span>{' '}
              {shortlist.reason}
            </div>
          )}
        </div>

        <div className="flex min-w-[180px] flex-col gap-2">
          {isMatched ? (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              {isZh ? '这位导师已经被接受用于你的个案。' : 'This tutor has already been accepted for your case.'}
            </div>
          ) : canRespond ? (
            <>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectAction(shortlist, 'accepted');
                }}
                disabled={isUpdating}
                className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isZh ? '接受导师' : 'Accept Tutor'}
              </button>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectAction(shortlist, 'declined');
                }}
                disabled={isUpdating}
                className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isZh ? '拒绝导师' : 'Reject Tutor'}
              </button>
            </>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              {shortlist.status === 'declined'
                ? isZh ? '这位已 shortlist 的导师已经被拒绝。' : 'This shortlisted tutor has already been rejected.'
                : isZh ? '我们仍在与这位导师协调中。' : 'We are still coordinating with this tutor.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CaseDetailPage: React.FC = () => {
  const { locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') ?? undefined;
  const { data, isLoading, error } = useCaseDetail(id, source);
  const { firebaseUser, profile } = useAuthContext();
  const isZh = locale === 'zh-CN';
  const [shortlistActionTarget, setShortlistActionTarget] = useState<{
    shortlist: ParentCaseShortlistData;
    action: ParentShortlistRespondRequest['status'];
  } | null>(null);

  const isParentViewingOwnCase = source === 'parent_my_cases' && profile?.role === 'parent';

  const { data: completionStatus } = useTutorCompletionStatus(
    firebaseUser?.uid,
    profile?.role === 'tutor'
  );

  const {
    data: shortlistData,
    isLoading: isShortlistLoading,
    error: shortlistError,
  } = useParentCaseShortlists(isParentViewingOwnCase ? id : undefined, { page: 1, limit: 20 });
  const respondToParentShortlist = useRespondToParentShortlistMutation();

  const shortlists = useMemo(
    () => shortlistData?.shortlist ?? [],
    [shortlistData?.shortlist]
  );

  const mockTutor: Tutor = {
    id: 'T001',
    fullName: 'Sarah Tan',
    profilePicture: 'https://ui-avatars.com/api/?name=Sarah+Tan&background=6366f1&color=fff&size=200',
    rating: 4.9,
    hourlyRate: '55-70',
    yearsOfTuitionExperience: 5,
    subjects: ['Mathematics', 'Additional Mathematics', 'Physics'],
    aboutMe: 'Experienced tutor specializing in Form 4-5 Mathematics and Sciences.',
    education: 'Bachelor of Science (Mathematics), University of Malaya',
    verified: true,
    nationality: 'MY',
    occupation: 'Tutor',
    tuitionType: 'Physical',
    preferredDistricts: ['Petaling'],
    availability: { Monday: ['Morning'], Wednesday: ['Evening'] },
    teachingLanguages: ['English'],
    examResults: [],
    teachingStyle: { iAm: [], iCan: [], iKnow: [], others: '' },
    gender: 'female',
    university: 'University of Malaya',
    location: 'Petaling Jaya',
  };

  if (isLoading) {
    return <CaseDetailSkeleton />;
  }

  if (error || !data?.case) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-12">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center font-medium text-primary hover:text-primary-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isZh ? '返回' : 'Go Back'}
          </button>

          <div className={`${staticCardClassName} mx-auto max-w-2xl p-8 text-center`}>
            <div className="mb-4 flex justify-center">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="mb-2 text-2xl font-bold text-gray-900">{isZh ? '找不到个案' : 'Case Not Found'}</h2>
            <p className="mb-6 text-gray-600">
              {error instanceof Error
                ? error.message
                : isZh ? '你查看的个案不存在，或已被移除。' : 'The case you are looking for does not exist or has been removed.'}
            </p>
            <button onClick={() => navigate('/cases')} className="btn-primary">
              {isZh ? '浏览全部个案' : 'Browse All Cases'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const caseData = data.case;
  const parentData = data.parent;
  const isTutorViewingOwnCase =
    profile?.role === 'tutor' && parentData.userId === profile.userId;
  const handleBack = () => {
    if (isParentViewingOwnCase) {
      navigate('/parent/my-cases');
      return;
    }

    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={handleBack}
          className="mb-6 flex items-center font-medium text-primary hover:text-primary-600"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {isParentViewingOwnCase ? (isZh ? '返回我的个案' : 'Back to My Cases') : (isZh ? '返回' : 'Go Back')}
        </button>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className={`${staticCardClassName} p-6`}>
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-2 flex items-center space-x-3">
                    <h1 className="text-3xl font-bold text-gray-900">
                      {localizeText(locale, caseData.level)} - {localizeText(locale, caseData.subject)}
                    </h1>
                  </div>
                  <p className="mb-4 text-sm text-gray-500">
                    {caseData.id} · {isZh ? '发布于' : 'Posted'} {new Date(caseData.createdAt).toLocaleDateString(locale === 'zh-CN' ? 'zh-CN' : 'en-US')}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                      {localizeText(locale, caseData.subject)}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="mb-1 text-3xl font-bold text-primary">
                    RM {caseData.hourlyRate || (isZh ? '待定' : 'TBD')}
                  </div>
                  <span className="text-sm text-gray-600">{isZh ? '每小时' : 'per hour'}</span>
                </div>
              </div>
            </div>

            {isParentViewingOwnCase && (
              <div className={`${staticCardClassName} p-6`}>
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{isZh ? '已 shortlist 的导师' : 'Shortlisted Tutors'}</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      {isZh ? '查看我们为此个案 shortlist 的导师，并决定你想继续推进哪一位。' : 'Review the tutors we shortlisted for this case and decide who you want to move forward with.'}
                    </p>
                  </div>
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                    {isZh ? `${shortlists.length} 位已 shortlist` : `${shortlists.length} shortlisted`}
                  </span>
                </div>

                {shortlistError && (
                  <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {shortlistError instanceof Error
                      ? shortlistError.message
                      : isZh ? '无法加载已 shortlist 的导师。' : 'Unable to load shortlisted tutors.'}
                  </div>
                )}

                {isShortlistLoading ? (
                  <div className="py-8 text-center text-gray-500">{isZh ? '正在加载已 shortlist 的导师...' : 'Loading shortlisted tutors...'}</div>
                ) : shortlists.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-gray-600">
                    {isZh ? '这个个案目前还没有 shortlist 任何导师。' : 'No tutors have been shortlisted for this case yet.'}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {shortlists.map((shortlist) => (
                      <ParentShortlistCard
                        key={shortlist.id}
                        shortlist={shortlist}
                        caseMatchedTutorId={caseData.matchedTutorId}
                        onOpenTutorProfile={(tutorId) =>
                          navigate(
                            `/tutor/${tutorId}?source=parent_my_cases_shortlist&returnTo=${encodeURIComponent(
                              `/cases/${caseData.id}?source=parent_my_cases`
                            )}`
                          )
                        }
                        onSelectAction={(selectedShortlist, action) => {
                          setShortlistActionTarget({ shortlist: selectedShortlist, action });
                        }}
                        isUpdating={respondToParentShortlist.isPending}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className={`${staticCardClassName} p-6`}>
              <h2 className="mb-4 text-xl font-bold text-gray-900">{isZh ? '课程资料' : 'Class Information'}</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-start space-x-3">
                  <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{isZh ? '地点' : 'Location'}</h3>
                    {caseData.district && <p className="text-gray-600">{caseData.district}</p>}
                    <p className="text-sm text-gray-500">{localizeText(locale, caseData.state)}</p>
                    {caseData.areaNote && <p className="text-xs text-gray-400">{caseData.areaNote}</p>}
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{isZh ? '频率' : 'Frequency'}</h3>
                    <p className="text-gray-600">{isZh ? `每周 ${caseData.lessonsPerWeek} 次` : `${caseData.lessonsPerWeek}x per week`}</p>
                    <p className="text-sm text-gray-500">{isZh ? `每节 ${caseData.lessonDuration} 小时` : `${caseData.lessonDuration} hours per lesson`}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{isZh ? '首选时段' : 'Preferred Times'}</h3>
                    <p className="text-gray-600">
                      {Object.keys(caseData.preferredTimes).length > 0
                        ? Object.entries(caseData.preferredTimes)
                            .map(([day, times]) => `${day}: ${times.join(', ')}`)
                            .join('; ')
                        : isZh ? '可灵活安排' : 'Flexible'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <GraduationCap className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{isZh ? '授课方式' : 'Teaching Method'}</h3>
                    <p className="text-gray-600">
                      {caseData.tuitionType === 'physical' ? (isZh ? '线下' : 'Physical') : (isZh ? '线上' : 'Online')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {caseData.tuitionType === 'physical'
                        ? isZh
                          ? `在${caseData.district || localizeText(locale, caseData.state)}学生地点面授`
                          : `Face-to-face at student's location in ${caseData.district || caseData.state}`
                        : isZh
                          ? '通过线上会议软件授课（如 Zoom、Google Meet 等）'
                          : 'Online via video conferencing (Zoom, Google Meet, etc.)'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className={`${staticCardClassName} p-6`}>
              <h2 className="mb-4 flex items-center text-xl font-bold text-gray-900">
                <MessageSquare className="mr-2 h-5 w-5" />
                {isZh ? '补充说明' : 'Additional Remarks'}
              </h2>
              <p className="rounded-lg bg-gray-50 p-4 leading-relaxed text-gray-700">
                {caseData.tutorRemarks || (isZh ? '暂无补充说明' : 'No additional remarks provided')}
              </p>
            </div>

            <div className={`${staticTintedCardClassName} p-6`}>
              <h2 className="mb-4 text-xl font-bold text-gray-900">{isZh ? '家长资料' : 'Parent Information'}</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-start space-x-3">
                  <Users className="mt-1 h-5 w-5 flex-shrink-0 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{isZh ? '家长姓名' : 'Parent Name'}</h3>
                    <p className="text-gray-700">{parentData.fullName}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{isZh ? '国籍' : 'Nationality'}</h3>
                    <p className="text-gray-700">{parentData.nationality}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-20 z-sticky">
              {isParentViewingOwnCase ? (
                <>
                  <div className={`${staticCardClassName} p-6`}>
                    <h3 className="mb-4 text-lg font-bold text-gray-900">{isZh ? '个案状态' : 'Case Status'}</h3>
                    <div className="space-y-3 text-sm text-gray-600">
                      <div className="flex items-center justify-between">
                        <span>{isZh ? '当前状态' : 'Current status'}</span>
                        <span className="font-semibold text-gray-900">{localizeText(locale, caseData.status.replaceAll('_', ' '))}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>{isZh ? '已 shortlist 导师' : 'Shortlisted tutors'}</span>
                        <span className="font-semibold text-gray-900">{shortlists.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>{isZh ? '已配对导师' : 'Matched tutor'}</span>
                        <span className="font-semibold text-gray-900">
                          {caseData.matchedTutorId ? (isZh ? '已确认' : 'Confirmed') : (isZh ? '尚未' : 'Not yet')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={`${staticTintedCardClassName} mt-4 p-4`}>
                    <div className="flex items-start space-x-2">
                      <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                      <div className="text-sm">
                        <p className="mb-1 font-semibold text-blue-900">{isZh ? '接下来会怎样？' : 'What happens next?'}</p>
                        <p className="text-blue-700">
                          {isZh ? '接受导师后，个案会进入付款跟进阶段。拒绝导师则会保持个案开放，以继续配对。' : 'Accepting a tutor moves the case to payment follow-up. Rejecting a tutor keeps the case open so matching can continue.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`${staticCardClassName} p-6`}>
                    <div className="mb-4 text-center">
                      <img
                        src={mockTutor.profilePicture}
                        alt={mockTutor.fullName}
                        className="mx-auto mb-3 h-24 w-24 rounded-full border-4 border-primary/10"
                      />
                      <div className="mb-1 flex items-center justify-center space-x-2">
                        <h3 className="text-xl font-bold text-gray-900">{mockTutor.fullName}</h3>
                        {mockTutor.verified && <CheckCircle className="h-5 w-5 text-green-500" />}
                      </div>
                      <div className="mb-2 flex items-center justify-center space-x-1 text-yellow-500">
                        <Star className="h-4 w-4 fill-current" />
                        <span className="text-sm font-semibold text-gray-900">{mockTutor.rating}</span>
                      </div>
                    </div>

                    <div className="mb-5 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center text-gray-600">
                          <DollarSign className="mr-1 h-4 w-4" />
                          {isZh ? '时薪' : 'Rate'}
                        </span>
                        <span className="font-semibold text-gray-900">{mockTutor.hourlyRate}/hr</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center text-gray-600">
                          <Award className="mr-1 h-4 w-4" />
                          {isZh ? '经验' : 'Experience'}
                        </span>
                        <span className="font-semibold text-gray-900">
                          {mockTutor.yearsOfTuitionExperience}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center text-gray-600">
                          <GraduationCap className="mr-1 h-4 w-4" />
                          {isZh ? '学历' : 'Education'}
                        </span>
                        <span className="text-right text-xs font-semibold text-gray-900">UM</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="mb-2 text-sm font-semibold text-gray-900">{isZh ? '科目' : 'Subjects'}</h4>
                      <div className="flex flex-wrap gap-1">
                        {mockTutor.subjects.map((subject, idx) => (
                          <span
                            key={idx}
                            className="rounded-full bg-secondary/10 px-2 py-1 text-xs text-secondary"
                          >
                            {subject}
                          </span>
                        ))}
                      </div>
                    </div>

                    <p className="mb-5 text-sm text-gray-600">{mockTutor.aboutMe}</p>

                    {firebaseUser ? (
                      <>
                        {isTutorViewingOwnCase ? (
                          <div className="mb-2 flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-600">
                            <AlertCircle className="h-5 w-5 flex-shrink-0" />
                            <div className="text-left text-sm">
                              <p className="font-medium">{isZh ? '你不能申请自己的个案。' : 'You cannot apply to your own case.'}</p>
                            </div>
                          </div>
                      ) : profile?.role === 'tutor' && !completionStatus?.canApply ? (
                          <div className="mb-2 flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-600">
                            <AlertCircle className="h-5 w-5 flex-shrink-0" />
                            <div className="text-left text-sm">
                              <p className="font-medium">{isZh ? '暂时不能申请' : 'Cannot apply yet'}</p>
                              {completionStatus?.completionPercentage !== 100 && (
                                <p className="text-xs">
                                  {isZh ? `请先完成资料（已完成 ${completionStatus?.completionPercentage}%）` : `Complete your profile (${completionStatus?.completionPercentage}% done)`}
                                </p>
                              )}
                              {completionStatus?.verificationStatus !== 'approved' && (
                                <p className="text-xs">{isZh ? '必须先通过管理员审核' : 'Must be verified by admin'}</p>
                              )}
                            </div>
                          </div>
                        ) : profile?.role !== 'tutor' ? (
                          <div className="mb-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 text-center">
                            <p className="text-sm text-gray-600">{isZh ? '只有导师可以申请课程。' : 'Only tutors can apply for classes.'}</p>
                          </div>
                        ) : (
                          <button className="mb-2 w-full btn-primary py-3">{isZh ? '申请此课程' : 'Apply for This Class'}</button>
                        )}
                        <button className="w-full btn-outline py-3">{isZh ? '查看导师资料' : 'View Tutor Profile'}</button>
                      </>
                    ) : (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 text-center">
                        <p className="text-sm text-gray-600">{isZh ? '请先登录以申请此课程。' : 'Please log in to apply for this class.'}</p>
                      </div>
                    )}
                  </div>

                  <div className={`${staticTintedCardClassName} mt-4 p-4`}>
                    <div className="flex items-start space-x-2">
                      <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                      <div className="text-sm">
                        <p className="mb-1 font-semibold text-blue-900">{isZh ? '安全可靠' : 'Safe & Secure'}</p>
                        <p className="text-xs text-blue-700">
                          {isZh ? '所有导师都经过审核，你的资料也会受到保护。' : 'All tutors are verified. Your information is protected.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!shortlistActionTarget}
        onClose={() => setShortlistActionTarget(null)}
        onConfirm={() => {
          if (!shortlistActionTarget) return;

          respondToParentShortlist.mutate(
            {
              shortlistId: shortlistActionTarget.shortlist.id,
              request: { status: shortlistActionTarget.action },
            },
            {
              onSuccess: () => setShortlistActionTarget(null),
            }
          );
        }}
        title={
          shortlistActionTarget?.action === 'accepted' ? (isZh ? '接受这位导师' : 'Accept This Tutor') : (isZh ? '拒绝这位导师' : 'Reject This Tutor')
        }
        message={
          shortlistActionTarget?.action === 'accepted' ? (
            <p>
              {isZh ? <>接受 <strong>{shortlistActionTarget.shortlist.tutorName}</strong> 后，此导师将会确认用于你的个案，个案也会进入下一步付款流程。</> : <>Accepting <strong>{shortlistActionTarget.shortlist.tutorName}</strong> will confirm this tutor for your case and move the case into the next payment step.</>}
            </p>
          ) : (
            <p>
              {isZh ? <>拒绝 <strong>{shortlistActionTarget?.shortlist.tutorName}</strong> 后，他们将不再被纳入考虑，系统会继续为你配对其他导师。</> : <>Rejecting <strong>{shortlistActionTarget?.shortlist.tutorName}</strong> will remove them from consideration and let matching continue with other tutors.</>}
            </p>
          )
        }
        confirmLabel={shortlistActionTarget?.action === 'accepted' ? (isZh ? '确认接受' : 'Yes, Accept') : (isZh ? '确认拒绝' : 'Yes, Reject')}
        cancelLabel={isZh ? '取消' : 'Cancel'}
        confirmVariant={shortlistActionTarget?.action === 'accepted' ? 'primary' : 'danger'}
        isLoading={respondToParentShortlist.isPending}
      />
    </div>
  );
};

export default CaseDetailPage;
